import { isWeb } from '@global/screens';
import {
  LIST_REFETCH_ON_CHANGE,
  withReapplyRefresh,
} from '@constants/listFetching';

// Standard consumption wrapper for a resource's RTK Query list endpoints.
// Owns the policy that's identical on every list screen so screens don't repeat it:
//  - re-apply refresh (refreshTick via withReapplyRefresh)
//  - refetchOnMountOrArgChange (cache vs always-refresh)
//  - the web (page-numbers) vs mobile (infinite-scroll) query switch
//
// The screen supplies only what's genuinely its own: the resource's three generated
// hooks, the scoped `params` (for refreshTick), and the `arg` (which carries the
// screen's own filter transform, e.g. SKP's apiFilter vs Brand's params.filter).
//
// `arg` shape: { filter, skip, limit, sort }. The infinite query drops `skip`
// (it paginates via pageParam); the count query uses `filter` only.
//
// Returns: { list, count, listQuery, infiniteQuery }
//   `list` is the active query for the platform (web → page query, mobile → infinite).
export default function useListQueries({
  listHook,
  countHook,
  infiniteHook,
  arg,
  params,
  skip = false,
}) {
  const opts = { refetchOnMountOrArgChange: LIST_REFETCH_ON_CHANGE };

  const listQuery = listHook(withReapplyRefresh(arg, params), {
    ...opts,
    skip: !isWeb || skip,
  });

  const infiniteQuery = infiniteHook(
    withReapplyRefresh(
      { filter: arg.filter, sort: arg.sort, limit: arg.limit },
      params,
    ),
    { ...opts, skip: isWeb || skip },
  );

  const count = countHook(withReapplyRefresh({ filter: arg.filter }, params), {
    ...opts,
    skip,
  });

  return {
    list: isWeb ? listQuery : infiniteQuery,
    count,
    listQuery,
    infiniteQuery,
  };
}
